import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { deleteUser } from '../../../../services/user'
import { getFullName } from '../../../../helpers/users'
import styles from './DeleteUserModal.module.scss'
import { setUserBanner } from '../../../../store/reducers/userBannerSlice'

const DeleteUserModal = ({ showModal, handleClose, user, onDeleteFailure, onNewRequest }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const editStyle = {
    backgroundColor: '#eb1700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '120px',

    span: {
      margin: '0 !important',
    },

    '&:hover': {
      backgroundColor: '#CC1400',
    },
    '&:disabled': {
      backgroundColor: '#665A47',
    },
    '&:focus': {
      backgroundColor: '#CC1400',
    },
  }

  const cancelStyle = {
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
    '&:focus': {
      backgroundColor: '#FFFFFF',
    },
  }

  const handleAfterUserDeletedSuccess = () => {
    setIsLoading(false)
    dispatch(setUserBanner({ message: `${getFullName(user)} was successfully deleted` }))
    navigate('/search-user')
  }

  const handleAfterUserDeletedFailure = () => {
    setIsLoading(false)
    handleClose()
    onDeleteFailure()
  }

  const handleDeleteUser = () => {
    if (isLoading) {
      return
    }
    setIsLoading(true)
    onNewRequest()
    deleteUser(user?.id)
      .then(handleAfterUserDeletedSuccess)
      .catch(handleAfterUserDeletedFailure)
  }

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        className: styles.dialogContainer,
      }}
    >
      <DialogActions className={styles.closeButtonContainer}>
        <Button onClick={handleClose} endIcon={<CloseIcon />} variant="text">
          Close
        </Button>
      </DialogActions>
      <DialogTitle className={styles.dialogTitle} id="delete-user-dialog-title">
        {` Are you sure you want to delete ${getFullName(user)}?`}
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <DialogContentText
          className={styles.dialogText}
          id="delete-user-dialog-text"
        >
          This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions className={styles.dialogActionButtons}>
        <Button
          className="primary-btn"
          onClick={handleDeleteUser}
          variant="contained"
          startIcon={
            isLoading && <CircularProgress size={26} sx={{ color: '#FFF' }} />
          }
          disableElevation
          sx={{ ...editStyle, marginTop: '20px' }}
          data-testid="managerDeleteUserModalConfirm"
        >
          {isLoading ? '' : 'Yes, delete user'}
        </Button>
        <Button
          onClick={handleClose}
          className="secondary-btn"
          variant="contained"
          disableElevation
          sx={{
            ...cancelStyle,
            marginTop: '20px',
            color: '#000',
            border: '1px solid #000',
          }}
          data-testid="managerDeleteUserModalCancel"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

DeleteUserModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  onDeleteFailure: PropTypes.func.isRequired,
  onNewRequest: PropTypes.func.isRequired,
}

export default DeleteUserModal
